import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import SolarIcon from 'components/icons/SolarIcon';

export interface WarrantySerialNumbersHandle {
    focus: () => void;
}

interface WarrantySerialNumbersProps {
    value: string[];
    disabled?: boolean;
    onChange: (serialNumbers: string[]) => void;
}

const WarrantySerialNumbers = forwardRef<WarrantySerialNumbersHandle, WarrantySerialNumbersProps>(({
    value,
    disabled = false,
    onChange,
}, ref) => {
    const { t } = useTranslation('Warranties');
    const inputs = useRef<(HTMLInputElement | null)[]>([]);
    const pendingFocus = useRef<number | null>(null);
    const rows = value.length ? value : [''];

    useEffect(() => {
        if (pendingFocus.current == null) return;
        const index = pendingFocus.current;
        pendingFocus.current = null;
        inputs.current[index]?.focus();
    }, [value]);

    useImperativeHandle(ref, () => ({
        focus: () => inputs.current[0]?.focus(),
    }));

    const addAfter = (index: number) => {
        const next = [...rows];
        next.splice(index + 1, 0, '');
        pendingFocus.current = index + 1;
        onChange(next);
    };

    const removeAt = (index: number) => {
        if (rows.length === 1) {
            pendingFocus.current = 0;
            onChange(['']);
            return;
        }
        pendingFocus.current = Math.max(0, index - 1);
        onChange(rows.filter((_, item) => item !== index));
    };

    return (
        <div className="warranty-serials">
            {rows.map((serial, index) => (
                <div className="warranty-serials__row" key={index}>
                    <Form.Control
                        ref={(element: HTMLInputElement | null) => {
                            inputs.current[index] = element;
                        }}
                        className="warranty-serials__input"
                        maxLength={255}
                        value={serial}
                        disabled={disabled}
                        aria-label={t('fields.serialNumber')}
                        onChange={(event) => {
                            const next = [...rows];
                            next[index] = event.target.value;
                            onChange(next);
                        }}
                        onKeyDown={(event) => {
                            if (event.key !== 'Enter') return;
                            event.preventDefault();
                            addAfter(index);
                        }}
                    />
                    <div className="warranty-serials__actions">
                        <button
                            type="button"
                            className="btn btn-link p-0 text-muted warranty-serials__action"
                            aria-label={t('serialAdd')}
                            onClick={() => addAfter(index)}
                        >
                            <SolarIcon name="plus" />
                        </button>
                        <button
                            type="button"
                            className="btn btn-link p-0 text-muted warranty-serials__action"
                            aria-label={t('serialRemove')}
                            onClick={() => removeAt(index)}
                        >
                            <SolarIcon name="trash" />
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
});

WarrantySerialNumbers.displayName = 'WarrantySerialNumbers';

export default WarrantySerialNumbers;
